import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import { RouletteSpeed } from '../../../models/communication/app-state.model';
import { DealerService } from '../../../services/dealer.service';

interface SpeedValues {
    current: number;
    min: number;
    max: number;
}

interface SpeedZoneWidths {
    min: number;
    safe: number;
    current: number;
    max: number;
    status: 'high' | 'low' | 'safe';
}

@Component({
    standalone: true,
    selector: 'app-roulette-velocity-display',
    imports: [CommonModule],
    templateUrl: './velocity-display.component.html',
    styleUrls: ['./velocity-display.component.scss']
})
export class RouletteVelocityDisplayComponent implements OnInit, OnDestroy {
    ballSpeed: SpeedValues = { current: 0, min: 25, max: 160 };
    wheelSpeed: SpeedValues = { current: 0, min: 2, max: 160 };

    ballZoneWidths: SpeedZoneWidths = { min: 0, safe: 0, current: 0, max: 0, status: 'low' };
    wheelZoneWidths: SpeedZoneWidths = { min: 0, safe: 0, current: 0, max: 0, status: 'low' };

    readonly gauges = [
        { label: 'Cylinder velocity', speed: this.wheelSpeed, zone: this.wheelZoneWidths },
        { label: 'Ball velocity', speed: this.ballSpeed, zone: this.ballZoneWidths }
    ];

    private readonly maxGaugeValue = 180;
    private speedSubscription!: Subscription;

    constructor(private readonly dealerService: DealerService) {}

    ngOnInit(): void {
        this.speedSubscription = this.dealerService.getRouletteSpeedObservable().subscribe((event: RouletteSpeed) => {
            this.ballSpeed = event.ballSpeed;
            this.wheelSpeed = event.wheelSpeed;

            this.ballZoneWidths = this.calculateZoneWidths(this.ballSpeed);
            this.wheelZoneWidths = this.calculateZoneWidths(this.wheelSpeed);

            this.gauges[0].speed = this.wheelSpeed;
            this.gauges[0].zone = this.wheelZoneWidths;

            this.gauges[1].speed = this.ballSpeed;
            this.gauges[1].zone = this.ballZoneWidths;
        });
    }

    ngOnDestroy(): void {
        this.speedSubscription.unsubscribe();
    }

    private clamp(value: number): number {
        return Math.min(Math.max(0, value), this.maxGaugeValue);
    }

    private calculateZoneWidths(speed: SpeedValues): SpeedZoneWidths {
        const { current, min, max } = speed;
        const range = this.maxGaugeValue;
        const multiplier = 100;

        let status: 'high' | 'low' | 'safe';
        if (current < min) {
            status = 'low';
        } else if (current > max) {
            status = 'high';
        } else {
            status = 'safe';
        }

        const minPct = (this.clamp(min) / range) * multiplier;
        const maxPct = (this.clamp(max) / range) * multiplier;

        const currentPct = (this.clamp(current) / range) * multiplier;

        return {
            min: minPct,
            safe: maxPct - minPct,
            current: currentPct,
            max: maxPct,
            status
        };
    }
}
